import React from 'react';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Paper } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Skeleton from '@material-ui/lab/Skeleton';
import { Row, Col, Label } from 'reactstrap';
import moment from 'moment';
import CustomSelect from 'components/CustomSelect';
import CustomInput from 'views/admin/Common/CustomInput';
import CustomButton from 'components/CustomButton';
import BillReceiptLinks from 'views/admin/Common/BillReceiptLinks';
import Page from 'views/admin/Common/Pagination';
import CommonTooltip from 'views/admin/Common/CommonTooltip';
import { getGlobalSchoolUtils, formResMessage } from 'utils/commonFunc';
import { fetchStudentClassDirectoryApi } from 'actions/admin/students';
import { showToast } from 'actions/admin/auth';
import colors from 'config/colors';
import StatTile from 'views/admin/Common/StatTile';
import {
  fetchFinanceSummaryApi,
  fetchFinanceTransactionsApi,
  fetchFinanceTransactionsPdfService,
} from 'actions/admin/financeReport';
import EmptyState from 'views/admin/Common/EmptyState';
import ClassFeeReport from './ClassFeeReport';
import ReportPicker, { usePersistedReport } from 'views/admin/Common/ReportPicker';

const FINANCE_VIEWS = [
  {
    key: 'collections',
    label: 'COLLECTIONS',
    icon: 'fas fa-rupee-sign',
    description: 'What came in and went out, by day, week or month',
  },
  {
    key: 'class',
    label: 'CLASS-WISE FEES',
    icon: 'fas fa-user-graduate',
    description: 'Which student of a class has paid and who has not',
  },
];

// The backend genuinely supports Weekly/Monthly/Quarterly/Half-Yearly/Yearly/Custom (confirmed by
// reading SchoolFinanceReportController / TransactionDetailReportController directly), but its
// WEEKLY means a raw ISO-8601 week-of-year (Monday-Sunday, numbered 1-53 across the whole calendar
// year, per DateRangeUtil.resolve - verified directly) - meaningless to a school admin picking "the
// 3rd week of September". So neither "Daily" nor our "Weekly" are sent to the backend as those enum
// values: both are resolved to plain calendar dates on the FE (Daily = a single day, Weekly = a
// day-bucket within the chosen month - Week 1 = days 1-7, Week 2 = 8-14, etc., a 5th bucket only for
// months with 29+ days) and sent as CUSTOM with fromDate/toDate, the same trick the fixed "Today"
// card above already uses. Bonus: this also makes the summary work for Weekly, which the backend's
// real WEEKLY period type can't do at all (its controller has no `week` param).
const ALL_PERIOD_TYPES = [
  { label: 'Daily', value: 'DAILY' },
  { label: 'Weekly', value: 'WEEKLY' },
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'Quarterly', value: 'QUARTERLY' },
  { label: 'Half Yearly', value: 'HALF_YEARLY' },
  { label: 'Yearly', value: 'YEARLY' },
  { label: 'Custom', value: 'CUSTOM' },
];
// Only these four are offered in the picker for now - Quarterly/Half-Yearly/Yearly stay fully wired
// up below (their fields, params and validation all still work) so re-enabling one later is just
// adding its value back to this list, nothing else to build.
const ENABLED_PERIOD_TYPE_VALUES = ['DAILY', 'WEEKLY', 'MONTHLY', 'CUSTOM'];
const PERIOD_TYPES = ALL_PERIOD_TYPES.filter(pt => ENABLED_PERIOD_TYPE_VALUES.includes(pt.value));

const MONTHS = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 },
];

// Always exactly 4 weeks for any month, rather than a variable 4-or-5 - Week 4 just stretches to
// whatever the month's last day is (28 in a non-leap Feb, 29/30/31 elsewhere) instead of spilling a
// short extra "Week 5" bucket. Keeps the picker's shape identical every month.
const getWeekOptionsForMonth = (year, month) => {
  const daysInMonth = moment({ year, month: month - 1 }).daysInMonth();
  const bounds = [
    [1, 7],
    [8, 14],
    [15, 21],
    [22, daysInMonth],
  ];
  return bounds.map(([startDay, endDay], index) => {
    const from = moment({ year, month: month - 1, day: startDay });
    const to = moment({ year, month: month - 1, day: endDay });
    return {
      value: index + 1,
      label: `Week ${index + 1} (${from.format('MMM D')} - ${to.format('D')})`,
      from: from.format('YYYY-MM-DD'),
      to: to.format('YYYY-MM-DD'),
    };
  });
};

// One dropdown covering every week of every month in the year, rather than a Month select feeding a
// separate Week select - picking a week directly (its label already names the month) is one fewer
// step than picking a month first, then a week within it.
const getWeekOptionsForYear = year =>
  MONTHS.flatMap(({ value: month }) =>
    getWeekOptionsForMonth(year, month).map(week => ({
      ...week,
      value: `${month}-${week.value}`,
      month,
      weekOfMonth: week.value,
    }))
  );

const TRANSACTION_TYPES = [
  { label: 'All types', value: '' },
  { label: 'Fee Payment', value: 'FEE_PAYMENT' },
  { label: 'Transport Payment', value: 'TRANSPORT_PAYMENT' },
  { label: 'Custom Bill Payment', value: 'CUSTOM_BILL_PAYMENT' },
  { label: 'Fee Refund', value: 'FEE_REFUND' },
];

const formatEnumLabel = value =>
  value
    ? value
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '--';

const money = value => `₹${Number(value || 0).toFixed(2)}`;

// Custom is the one period type with no natural ceiling on its own (Weekly/Monthly/Quarterly/
// Half-Yearly/Yearly each cover a fixed, bounded span) - without a cap here someone could pick a
// 15-year range and fire an aggregation query over the school's entire history. Year is capped too,
// so a typo or an old-year pick doesn't quietly do the same thing via Yearly/Quarterly/etc.
const MAX_CUSTOM_RANGE_YEARS = 2;
// A calendar-year add (not a fixed day count) so the cap lands on the same date next year(s) rather
// than drifting by a day around leap years.
const maxCustomToDate = fromDate => moment(fromDate).add(MAX_CUSTOM_RANGE_YEARS, 'years').subtract(1, 'day');
const MIN_REPORT_YEAR = moment().year() - 5;
const MAX_REPORT_YEAR = moment().year();

// totalExpectedFee, totalConcessionAmount, netExpectedFeeAfterConcession and feePending were removed
// from this list on purpose (not an oversight) - verified directly in
// SchoolFinanceReportServiceImpl.buildIncomeDetails (school repo, lines 78-115): the expected-fee and
// concession-amount queries take no fromDate/toDate at all, so they return the exact same lifetime
// total no matter which period is selected - they are not "today's" or "this month's" figures.
// feePending is derived from that same non-period-scoped total minus the (correctly period-scoped)
// feeCollected, so it's a hybrid number that doesn't mean "pending for this period" either. Every row
// still listed below IS genuinely filtered by the selected period's fromDate/toDate in that same
// method (confirmed per-field).
const FEE_TRANSACTION_ROWS = [
  ['feeCollected', 'Fee Collected'],
  ['transportFeeCollected', 'Transport Fee Collected'],
  ['customBillRaised', 'Custom Bills Raised'],
  ['customBillCollected', 'Custom Bills Collected'],
  ['totalRefunds', 'Refunds (Deducted)'],
];

// Inventory is grouped as its own list. Items issued to students are billed through Custom Bills
// (billType INVENTORY) and so are already counted under the "Custom Bills Raised/Collected" rows
// on the Fee & Transactions side - there's no separate inventory income line. Salary Expense has
// no backing value yet (see ExpenseDetailsDTO's own comment) - an always-zero placeholder for when
// that lands, not a bug.
const INVENTORY_ROWS = [
  ['inventoryPurchaseTotal', 'Purchases (Total)'],
  ['inventoryPurchasePaid', 'Purchases (Paid)'],
  ['inventoryPurchaseBalance', 'Purchases (Owed)'],
  ['salaryExpense', 'Salary Expense (Coming soon)'],
];

const BreakdownList = ({ title, rows, data }) => (
  <div>
    <div style={{ fontWeight: 'bold', marginBottom: 6, color: colors.grey2, fontSize: 13 }}>{title}</div>
    {rows.map(([key, label]) => (
      <div
        key={key}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 13,
          padding: '6px 0',
          borderBottom: '1px solid #f0f0f0',
        }}>
        <span style={{ color: '#6b778a' }}>{label}</span>
        <span style={{ fontWeight: 600 }}>{money(data?.[key])}</span>
      </div>
    ))}
  </div>
);

// Placeholder versions matching the real StatTile/BreakdownList shapes - shown while a fetch is in
// flight (both on the debounced auto-refetch and the initial load) instead of a spinner that blanks
// out the whole card, so the layout doesn't jump around every time a period field changes.
const StatTileSkeleton = () => <StatTile loading />;

const BreakdownListSkeleton = ({ title, rowCount }) => (
  <div>
    <div style={{ fontWeight: 'bold', marginBottom: 6, color: colors.grey2, fontSize: 13 }}>{title}</div>
    {Array.from({ length: rowCount }).map((_, i) => (
      <div
        key={i}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '6px 0',
          borderBottom: '1px solid #f0f0f0',
        }}>
        <Skeleton variant="text" width="55%" height={16} />
        <Skeleton variant="text" width={50} height={16} />
      </div>
    ))}
  </div>
);

// Two separate figures, shown side by side rather than netted into one total (fee/transaction
// income and inventory expense are different things and shouldn't be summed into a single
// misleading number). Income from issuing inventory to students is tracked via Custom Bills, so
// it's part of the income figure above rather than a tile of its own.
const SummaryBlock = ({ summary, loading, unavailableNote }) => {
  if (unavailableNote) return <div style={{ color: '#9a9a9a', fontSize: 13 }}>{unavailableNote}</div>;
  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
          <StatTileSkeleton />
          <StatTileSkeleton />
        </div>
        <Row>
          <Col md={6}>
            <BreakdownListSkeleton title="Fee & Transactions" rowCount={FEE_TRANSACTION_ROWS.length} />
          </Col>
          <Col md={6}>
            <BreakdownListSkeleton title="Inventory" rowCount={INVENTORY_ROWS.length} />
          </Col>
        </Row>
      </>
    );
  }
  if (!summary) return <EmptyState compact icon="fas fa-chart-bar" title="No data for this period" />;
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <StatTile label="Income (Fees & Transactions)" value={money(summary.income?.totalIncome)} tone="income" />
        <StatTile
          label="Inventory Expense (Purchases)"
          value={money(summary.expense?.inventoryPurchaseTotal)}
          tone="expense"
        />
      </div>
      <Row>
        <Col md={6}>
          <BreakdownList title="Fee & Transactions" rows={FEE_TRANSACTION_ROWS} data={summary.income} />
        </Col>
        <Col md={6}>
          <BreakdownList title="Inventory" rows={INVENTORY_ROWS} data={summary.expense} />
        </Col>
      </Row>
    </>
  );
};

// A CustomSelect used alongside CustomInput in the same row - CustomInput wraps itself in a
// Bootstrap FormGroup (which carries its own bottom margin), CustomSelect doesn't, so left as-is
// the two fall out of vertical alignment in a flex row. Wrapping in the same "mb-3" idiom already
// used everywhere else in this codebase for Select fields keeps their heights matched.
const FieldSelect = ({ label, ...props }) => (
  <div className="mb-3">
    <Label>{label}</Label>
    <CustomSelect noLabel {...props} />
  </div>
);

// Same idea for the row's action buttons - an invisible label reserves the same header space so
// the buttons line up with the input BOXES next to them, not just the row's outer edge.
const FieldButton = ({ children }) => (
  <div className="mb-3">
    <Label>&nbsp;</Label>
    {children}
  </div>
);

// Finance reporting is three cards: a fixed "Today" snapshot (always the current day, no filters),
// a "Report" card where the admin picks any period - its summary and PDF download live together
// here instead of three separate cards - and the Transactions list, which defaults to today (it
// shares the same period state as Report) and can be filtered by type. Daily and Weekly are both
// resolved to plain CUSTOM date ranges on the FE (see ALL_PERIOD_TYPES above), which sidesteps the
// backend summary endpoint's real gap - it has no WEEKLY support at all (no `week` param). PDF
// export only supports Day/Month (a separate, smaller backend enum) - so the Download button only
// appears when the current period selection is a single day or a whole month.
const FinanceReportPage = () => {
  const dispatch = useDispatch();
  const loggedUserToken = useSelector(state => state.auth.loggedUserToken);
  const globalSchool = getGlobalSchoolUtils();
  const schoolId = globalSchool?.schoolId;
  const showWarning = (message, type) => dispatch(showToast(message, type));
  const today = moment().format('YYYY-MM-DD');

  // Two views of the money: what came in over a period (the default), and which student of a class has paid their fees
  const [view, setView] = usePersistedReport('financeReport', FINANCE_VIEWS, 'collections');
  const [todaySummary, setTodaySummary] = useState(null);
  const [todayLoading, setTodayLoading] = useState(false);

  // Defaults to Daily/today so the Report card and the Transactions list below both start out
  // showing "today" - matching the Today card above - until the admin picks a wider period.
  const [periodType, setPeriodType] = useState('DAILY');
  const [date, setDate] = useState(today);
  const [year, setYear] = useState(moment().year());
  const [month, setMonth] = useState(moment().month() + 1);
  const [quarter, setQuarter] = useState(Math.ceil((moment().month() + 1) / 3));
  const [half, setHalf] = useState(moment().month() < 6 ? 1 : 2);
  // Week-of-month (1-4), not an ISO week number - see getWeekOptionsForMonth. Defaults to whichever
  // bucket today's date falls into.
  const [weekOfMonth, setWeekOfMonth] = useState(Math.min(4, Math.ceil(moment().date() / 7)));
  // Defaults to month-to-date rather than today-today - a same-day range looks identical to Daily,
  // which hides the one thing Custom actually offers that Daily/Weekly/Monthly can't: an arbitrary
  // range. Month-to-date is a genuinely different, useful default ("how's this month going so far").
  const [fromDate, setFromDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(today);

  const [transactionType, setTransactionType] = useState('');
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [transactions, setTransactions] = useState(null);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  // studentId -> class name for the Student column (the transaction DTO only has the student's name)
  const [classByStudent, setClassByStudent] = useState({});
  const [loadedBatchIds, setLoadedBatchIds] = useState([]);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const fetchToday = () => {
    setTodayLoading(true);
    fetchFinanceSummaryApi({ schoolId, periodType: 'CUSTOM', fromDate: today, toDate: today, token: loggedUserToken })
      .then(res => setTodaySummary(res.data))
      .catch(err => showWarning(formResMessage(err), 'error'))
      .finally(() => setTodayLoading(false));
  };

  // Neither Daily nor our Weekly are real ReportPeriodType values on the backend (see the comment on
  // ALL_PERIOD_TYPES above) - both resolve to plain dates and are sent as CUSTOM.
  const apiPeriodType = periodType === 'DAILY' || periodType === 'WEEKLY' ? 'CUSTOM' : periodType;

  const buildPeriodParams = () => {
    switch (periodType) {
      case 'DAILY':
        return { fromDate: date, toDate: date };
      case 'WEEKLY': {
        const weekOption = getWeekOptionsForMonth(year, month).find(w => w.value === weekOfMonth);
        return { fromDate: weekOption.from, toDate: weekOption.to };
      }
      case 'MONTHLY':
        return { year, month };
      case 'QUARTERLY':
        return { year, quarter };
      case 'HALF_YEARLY':
        return { year, half };
      case 'YEARLY':
        return { year };
      case 'CUSTOM':
        return { fromDate, toDate };
      default:
        return {};
    }
  };

  const fetchSummary = () => {
    setSummaryLoading(true);
    fetchFinanceSummaryApi({ schoolId, periodType: apiPeriodType, token: loggedUserToken, ...buildPeriodParams() })
      .then(res => setSummary(res.data))
      .catch(err => showWarning(formResMessage(err), 'error'))
      .finally(() => setSummaryLoading(false));
  };

  // transactionType is passed explicitly (rather than always reading the transactionType state)
  // so the transaction-type dropdown's onChange can trigger a refetch with the just-picked value
  // immediately, without waiting a render for setTransactionType to land first.
  // Classes are only needed for cosmetics, so a failure just leaves the Student column without one
  // rather than blocking the list. Batches already loaded aren't fetched again on period/page change.
  const loadClasses = list => {
    const missing = [...new Set(list.map(t => t.batchId).filter(Boolean))].filter(id => !loadedBatchIds.includes(id));
    if (!missing.length) return;
    fetchStudentClassDirectoryApi({ batchIds: missing, token: loggedUserToken })
      .then(dir => {
        setClassByStudent(prev => ({
          ...prev,
          ...Object.fromEntries(Object.entries(dir).map(([id, entry]) => [id, entry.className])),
        }));
        setLoadedBatchIds(prev => [...prev, ...missing]);
      })
      .catch(() => {});
  };

  const fetchTransactions = (nextPage = 0, typeOverride = transactionType) => {
    setTransactionsLoading(true);
    fetchFinanceTransactionsApi({
      schoolId,
      periodType: apiPeriodType,
      transactionType: typeOverride,
      page: nextPage,
      size: 20,
      token: loggedUserToken,
      ...buildPeriodParams(),
    })
      .then(res => {
        setTransactions(res.data);
        loadClasses(res.data?.content || []);
      })
      .catch(err => showWarning(formResMessage(err), 'error'))
      .finally(() => setTransactionsLoading(false));
  };

  const fetchReportData = () => {
    if (periodType === 'CUSTOM' && moment(toDate).isAfter(maxCustomToDate(fromDate))) {
      showWarning(`Custom range is limited to ${MAX_CUSTOM_RANGE_YEARS} years at a time to keep reports fast`, 'error');
      return;
    }
    if (periodType !== 'CUSTOM' && periodType !== 'DAILY' && (year < MIN_REPORT_YEAR || year > MAX_REPORT_YEAR)) {
      showWarning(`Year must be between ${MIN_REPORT_YEAR} and ${MAX_REPORT_YEAR}`, 'error');
      return;
    }
    if (periodType === 'QUARTERLY' && (quarter < 1 || quarter > 4)) {
      showWarning('Quarter must be between 1 and 4', 'error');
      return;
    }
    if (periodType === 'HALF_YEARLY' && (half < 1 || half > 2)) {
      showWarning('Half must be 1 or 2', 'error');
      return;
    }
    fetchSummary();
    fetchTransactions(0);
  };

  useEffect(() => {
    if (schoolId) fetchToday();
    // eslint-disable-next-line
  }, [schoolId]);

  // Report/Transactions refetch on every period-defining change - no "View Report" button to click.
  // Debounced so typing into the Year field doesn't fire a request per keystroke; dropdown and date-
  // picker changes (Period Type, Month, Week, Daily's Date, Custom's From/To) already only fire
  // onChange with a complete value, so in practice they update almost immediately.
  useEffect(() => {
    if (!schoolId) return;
    const timer = setTimeout(fetchReportData, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [schoolId, periodType, date, year, month, weekOfMonth, quarter, half, fromDate, toDate]);

  const singleDayValue = periodType === 'DAILY' ? date : fromDate;
  const isSingleDay = periodType === 'DAILY' || (periodType === 'CUSTOM' && fromDate === toDate);
  // Human-readable label for whichever period is currently selected - shown next to
  // "Transactions" so it's clear the list below is scoped to that period, not every transaction
  // ever (same wording added to the mobile app's equivalent screen for consistency).
  const periodLabel = (() => {
    switch (periodType) {
      case 'DAILY':
        return moment(date).format('D MMM YYYY');
      case 'WEEKLY': {
        const weekOption = getWeekOptionsForMonth(year, month).find(w => w.value === weekOfMonth);
        return weekOption
          ? `${moment(weekOption.from).format('D MMM')} - ${moment(weekOption.to).format('D MMM YYYY')}`
          : '';
      }
      case 'MONTHLY':
        return moment({ year, month: month - 1 }).format('MMMM YYYY');
      case 'QUARTERLY':
        return `Q${quarter} ${year}`;
      case 'HALF_YEARLY':
        return `H${half} ${year}`;
      case 'CUSTOM':
        return `${moment(fromDate).format('D MMM YYYY')} - ${moment(toDate).format('D MMM YYYY')}`;
      default:
        return '';
    }
  })();
  // PDF export is backend-capped to Day/Month only (PdfReportPeriodType.java has exactly those two
  // values) - Weekly and a multi-day Custom range genuinely can't produce a PDF without a backend
  // change, so they fall through to the explanatory note below rather than a button that would fail.
  const pdfSupported = periodType === 'MONTHLY' || isSingleDay;
  // Gate on the period's summary totals, not the (possibly type-filtered) transactions list below -
  // the PDF always covers every transaction type regardless of what the on-page Transactions filter
  // is set to, so basing "is there anything to download" on that filtered list could disable the
  // button for a period that genuinely has data (just not of the currently-selected type), or the
  // reverse. Summary is fetched unfiltered every time the period changes, so it's the true signal.
  // Deliberately excludes inventory figures - both the Transactions list and the PDF are built from
  // TransactionType, a 4-value enum (FEE_PAYMENT/TRANSPORT_PAYMENT/CUSTOM_BILL_PAYMENT/FEE_REFUND)
  // that has no inventory entry at all, so an inventory-only period would enable a download that
  // opens to an empty report - confirmed live: a ₹6000 inventory purchase showed up in the Inventory
  // stat tile but not as a row in the Transactions table or the PDF.
  const periodHasActivity =
    !!summary &&
    [
      summary.income?.totalIncome,
      summary.income?.feeCollected,
      summary.income?.transportFeeCollected,
      summary.income?.customBillCollected,
      summary.income?.totalRefunds,
    ].some(v => Number(v) > 0);

  const downloadPdf = () => {
    setPdfDownloading(true);
    const params = isSingleDay
      ? { periodType: 'DAILY', date: singleDayValue, reportName: `Finance Report - ${singleDayValue}` }
      : { periodType: 'MONTHLY', year, month, reportName: `Finance Report - ${year}-${month}` };
    dispatch(fetchFinanceTransactionsPdfService({ schoolId, token: loggedUserToken, ...params }))
      .catch(err => showWarning(formResMessage(err), 'error'))
      .finally(() => setPdfDownloading(false));
  };

  // The reason (if any) Download Report can't run right now - shown both on hover (via CommonTooltip)
  // and as a toast on click. The button is kept genuinely clickable (only visually dimmed, not the
  // native `disabled` attribute) specifically so the click path works too - a truly disabled button
  // never fires onClick at all, which would leave a tap with no feedback on touch devices, or for
  // anyone who clicks before hovering.
  const downloadDisabledReason = !pdfSupported
    ? 'PDF export only supports a single Day or a Month'
    : !summaryLoading && !periodHasActivity
    ? 'No Fee & Transactions data for this period - Inventory activity isn’t part of this report'
    : '';

  const handleDownloadClick = () => {
    if (downloadDisabledReason) {
      showWarning(downloadDisabledReason, 'warning');
      return;
    }
    downloadPdf();
  };

  if (!schoolId) {
    return <div style={{ textAlign: 'center', color: '#9a9a9a', padding: 40 }}>Please select a school.</div>;
  }

  const rows = (transactions?.content || []).map(item => ({
    ...item,
    typeLabel: formatEnumLabel(item.transactionType),
    dateLabel: item.transactionDateTime ? moment(item.transactionDateTime).format('DD MMM, YYYY hh:mm A') : '--',
    amountLabel: (
      <b style={{ color: Number(item.netAmount) < 0 ? '#e53935' : colors.grey1 }}>{money(item.netAmount)}</b>
    ),
    statusLabel: formatEnumLabel(item.status),
  }));

  const viewSwitch = <ReportPicker items={FINANCE_VIEWS} value={view} onChange={setView} />;

  if (view === 'class') {
    return (
      <>
        {viewSwitch}
        <ClassFeeReport />
      </>
    );
  }
  return (
    <>
      {viewSwitch}
      <Paper elevation={0} style={{ padding: 20, border: '3px solid #e0e0e0', marginBottom: 16 }}>
        <div className="card-category font-weight-bold mb-3">Today &middot; {moment(today).format('DD MMM, YYYY')}</div>
        <SummaryBlock summary={todaySummary} loading={todayLoading} />
      </Paper>

      <div
        style={{
          margin: '24px 0 12px',
          fontWeight: 'bold',
          color: colors.grey2,
          fontSize: 13,
          textTransform: 'uppercase',
        }}>
        Report
      </div>

      <Paper elevation={0} style={{ padding: 20, border: '3px solid #e0e0e0', marginBottom: 16 }}>
        <Row>
          <Col sm={4} md={3}>
            <FieldSelect
              label="Period Type"
              name="periodType"
              value={periodType}
              options={PERIOD_TYPES}
              onChange={(n, v) => setPeriodType(v)}
            />
          </Col>
          {periodType === 'DAILY' && (
            <Col sm={4} md={2}>
              <CustomInput
                label="Date"
                name="date"
                type="date"
                max={today}
                value={date}
                onChange={(n, v) => setDate(v)}
              />
            </Col>
          )}
          {periodType !== 'CUSTOM' && periodType !== 'DAILY' && (
            <Col sm={4} md={2}>
              <CustomInput
                label="Year"
                name="year"
                type="number"
                min={MIN_REPORT_YEAR}
                max={MAX_REPORT_YEAR}
                value={year}
                onChange={(n, v) => setYear(Number(v))}
              />
            </Col>
          )}
          {periodType === 'MONTHLY' && (
            <Col sm={4} md={2}>
              <FieldSelect label="Month" name="month" value={month} options={MONTHS} onChange={(n, v) => setMonth(v)} />
            </Col>
          )}
          {periodType === 'WEEKLY' && (
            <Col sm={4} md={3}>
              <FieldSelect
                label="Week"
                name="weekSelection"
                value={`${month}-${weekOfMonth}`}
                options={getWeekOptionsForYear(year)}
                onChange={(n, v) => {
                  const [m, w] = v.split('-').map(Number);
                  setMonth(m);
                  setWeekOfMonth(w);
                }}
              />
            </Col>
          )}
          {periodType === 'QUARTERLY' && (
            <Col sm={4} md={2}>
              <CustomInput
                label="Quarter (1-4)"
                name="quarter"
                type="number"
                min={1}
                max={4}
                value={quarter}
                onChange={(n, v) => setQuarter(Number(v))}
              />
            </Col>
          )}
          {periodType === 'HALF_YEARLY' && (
            <Col sm={4} md={2}>
              <CustomInput
                label="Half (1 or 2)"
                name="half"
                type="number"
                min={1}
                max={2}
                value={half}
                onChange={(n, v) => setHalf(Number(v))}
              />
            </Col>
          )}
          {periodType === 'CUSTOM' && (
            <>
              <Col sm={4} md={2}>
                <CustomInput
                  label="From date"
                  name="fromDate"
                  type="date"
                  max={today}
                  value={fromDate}
                  onChange={(n, v) => {
                    setFromDate(v);
                    // Keep the range valid (and within the cap) as the admin moves the start date,
                    // rather than only catching an out-of-range pick when they hit View Report.
                    const maxTo = maxCustomToDate(v);
                    if (moment(toDate).isBefore(v) || moment(toDate).isAfter(maxTo)) {
                      setToDate(moment.min(maxTo, moment(today)).format('YYYY-MM-DD'));
                    }
                  }}
                />
              </Col>
              <Col sm={4} md={2}>
                <CustomInput
                  label="To date"
                  name="toDate"
                  type="date"
                  min={fromDate}
                  max={moment.min(maxCustomToDate(fromDate), moment(today)).format('YYYY-MM-DD')}
                  value={toDate}
                  onChange={(n, v) => setToDate(v)}
                />
              </Col>
            </>
          )}
          <Col sm={4} md={3} style={{ alignSelf: 'end', marginBottom: 1.5 }}>
            <FieldButton>
              <div>
                {/* No "apply"/"view" button - the summary and transactions below refetch on their own
                    whenever a period field changes (debounced, see the effect above), so the only
                    button left here is the one action that genuinely needs an explicit click:
                    generating a PDF to print or save. Always rendered and clickable (dimmed rather
                    than natively disabled) - a real `disabled` button never fires onClick, so a tap
                    on it gives zero feedback; clicking it here shows the same explanation as the
                    hover tooltip via a toast, which also covers touch devices with no hover state. */}
                <CommonTooltip content={downloadDisabledReason}>
                  <CustomButton
                    name="Download Report"
                    variant="outlined"
                    disabled={pdfDownloading || summaryLoading}
                    loading={pdfDownloading}
                    onClick={handleDownloadClick}
                    style={{
                      backgroundColor: colors.accent,
                      color: colors.white,
                      opacity: downloadDisabledReason ? 0.5 : 1,
                    }}
                  />
                </CommonTooltip>
              </div>
            </FieldButton>
          </Col>
        </Row>
        <hr style={{ margin: '4px 0 20px' }} />
        <SummaryBlock summary={summary} loading={summaryLoading} />
      </Paper>

      <Paper elevation={0} style={{ padding: 20, border: '3px solid #e0e0e0' }}>
        <div className="card-category font-weight-bold mb-3">Transactions ({periodLabel})</div>
        <Row className="mb-3">
          <Col sm={4} md={3}>
            <CustomSelect
              noLabel
              name="transactionType"
              value={transactionType}
              options={TRANSACTION_TYPES}
              onChange={(n, v) => {
                setTransactionType(v);
                fetchTransactions(0, v);
              }}
            />
          </Col>
        </Row>
        <div style={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow style={{ backgroundColor: '#e0e0e0' }}>
                <TableCell component="th">
                  <b>Date</b>
                </TableCell>
                <TableCell component="th" align="left">
                  <b>Type</b>
                </TableCell>
                <TableCell component="th" align="left">
                  <b>Student</b>
                </TableCell>
                <TableCell component="th" align="left">
                  <b>Description</b>
                </TableCell>
                <TableCell component="th" align="left">
                  <b>Amount</b>
                </TableCell>
                <TableCell component="th" align="left">
                  <b>Mode</b>
                </TableCell>
                <TableCell component="th" align="left">
                  <b>Reference</b>
                </TableCell>
                <TableCell component="th" align="left">
                  <b>Status</b>
                </TableCell>
                <TableCell component="th" align="left">
                  <b>Receipt</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactionsLoading ? (
                // Skeleton rows keep the header/columns in place while refetching, instead of
                // swapping the whole table out for a spinner on every period change.
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length ? (
                rows.map(item => (
                  <TableRow key={`${item.transactionType}-${item.sourceRecordId}`}>
                    <TableCell align="left">{item.dateLabel}</TableCell>
                    <TableCell align="left">{item.typeLabel}</TableCell>
                    <TableCell align="left">
                      {item.studentName || '--'}
                      {classByStudent[item.studentId] && (
                        <span style={{ color: '#9a9a9a' }}> [{classByStudent[item.studentId]}]</span>
                      )}
                    </TableCell>
                    <TableCell align="left">{item.description || '--'}</TableCell>
                    <TableCell align="left">{item.amountLabel}</TableCell>
                    <TableCell align="left">{item.paymentMode || '--'}</TableCell>
                    <TableCell align="left">{item.transactionReference || '--'}</TableCell>
                    <TableCell align="left">{item.statusLabel}</TableCell>
                    <TableCell align="left">
                      {item.transactionType === 'CUSTOM_BILL_PAYMENT' ? (
                        <BillReceiptLinks billId={item.sourceRecordId} schoolId={schoolId} />
                      ) : (
                        '--'
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} component="td" style={{ textAlign: 'center', color: '#9a9a9a' }}>
                    No Data Found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {!!transactions?.totalElements && (
            <Page
              style={{ marginTop: 15 }}
              content={transactions.content}
              number={transactions.pageNumber}
              size={transactions.pageSize}
              totalElements={transactions.totalElements}
              totalPages={transactions.totalPages}
              isLoading={transactionsLoading}
              fetchPageData={uiPage => fetchTransactions(uiPage - 1)}
            />
          )}
        </div>
      </Paper>
    </>
  );
};

export default FinanceReportPage;
